import React from "react";
import { Link } from "react-router-dom";
import { base44 } from "../api/base44Client";
import { useQuery } from "@tanstack/react-query";
import { Building2, TrendingUp, CheckCircle, Shield, Zap, Users, ArrowRight, Star, MapPin, Phone } from "lucide-react";
import { motion } from "framer-motion";

export default function Accueil() {
  const { data: featuredProjects = [] } = useQuery({
    queryKey: ['featured-projects'],
    queryFn: () => base44.entities.Project.filter({ is_featured: true, visible_on_site: true }, '-created_date', 3),
  });

  const { data: activeCampaigns = [] } = useQuery({
    queryKey: ['active-campaigns'],
    queryFn: () => base44.entities.Campaign.filter({ status: 'active', visible_on_site: true }, '-created_date', 2),
  });

  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const all = await base44.entities.Settings.list();
      return all.length > 0 ? all[0] : null;
    },
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const { data: slides = [] } = useQuery({
    queryKey: ['home-slides'],
    queryFn: () => base44.entities.HomeSlide.filter({ active: true }, 'order'),
    enabled: !!settings?.home_carousel_enabled
  });

  const [currentSlide, setCurrentSlide] = React.useState(0);

  React.useEffect(() => {
    if (settings?.home_carousel_enabled && settings?.home_carousel_autoplay && slides.length > 1) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, settings.home_carousel_interval || 5000);
      return () => clearInterval(interval);
    }
  }, [settings, slides.length]);

  const features = [
    { icon: Shield, title: "Sécurité Garantie", description: "Tous nos terrains sont titrés et légalement sécurisés" },
    { icon: TrendingUp, title: "Investissement Rentable", description: "Préfinancement participatif avec retour sur investissement garanti" },
    { icon: Zap, title: "Processus Moderne", description: "Gestion digitale et suivi transparent de votre investissement" },
    { icon: Users, title: "Accompagnement Professionnel", description: "Équipe d'experts dédiée à votre service tout au long du processus" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero / Carousel */}
      {settings?.home_carousel_enabled && slides.length > 0 ? (
        <section className="relative bg-white overflow-hidden border-b-4 border-[#c41e3a]">
          <div className="relative h-[500px] md:h-[600px]">
            {slides.map((slide, index) => (
              <div key={slide.id} className={`absolute inset-0 transition-opacity duration-1000 ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`}>
                <img src={slide.image} alt={slide.title || 'Slide'} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/50"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center text-white max-w-4xl px-4">
                    {slide.title && <motion.h1 initial={{ opacity:0,y:30 }} animate={{ opacity:1,y:0 }} transition={{ duration:0.8 }} className="text-4xl md:text-6xl font-bold mb-4">{slide.title}</motion.h1>}
                    {slide.subtitle && <motion.p initial={{ opacity:0,y:20 }} animate={{ opacity:1,y:0 }} transition={{ delay:0.2,duration:0.8 }} className="text-2xl md:text-3xl mb-4 text-[#c41e3a]">{slide.subtitle}</motion.p>}
                    {slide.description && <motion.p initial={{ opacity:0,y:20 }} animate={{ opacity:1,y:0 }} transition={{ delay:0.4,duration:0.8 }} className="text-xl mb-8">{slide.description}</motion.p>}
                    {slide.button_text && slide.button_link && (
                      <motion.div initial={{ opacity:0,y:20 }} animate={{ opacity:1,y:0 }} transition={{ delay:0.6,duration:0.8 }}>
                        <Link to={slide.button_link}>
                          <button className="accent-gradient text-white px-8 py-4 text-lg rounded-lg hover:opacity-90 shadow-lg flex items-center gap-2 mx-auto">
                            {slide.button_text}<ArrowRight className="w-5 h-5" />
                          </button>
                        </Link>
                      </motion.div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {slides.length > 1 && (
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2">
              {slides.map((_, index) => (
                <button key={index} onClick={() => setCurrentSlide(index)}
                  className={`h-3 rounded-full transition-all ${index === currentSlide ? 'bg-[#c41e3a] w-8' : 'bg-white/50 w-3'}`} />
              ))}
            </div>
          )}
        </section>
      ) : (
        <section className="relative bg-white text-black py-20 md:py-32 overflow-hidden border-b-4 border-[#c41e3a]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div initial={{ opacity:0,y:30 }} animate={{ opacity:1,y:0 }} transition={{ duration:0.8 }} className="text-center max-w-4xl mx-auto">
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight text-black">
                L'Immobilier Participatif,<br />
                <span className="text-[#c41e3a]">Transparent et Accessible</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-700 mb-8">
                Investissez dans l'immobilier ivoirien en toute confiance.<br />
                Terrains titrés, maisons modernes et préfinancement sécurisé.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/prefinancements">
                  <button className="accent-gradient text-white px-8 py-4 text-lg rounded-lg hover:opacity-90 shadow-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />Participer à un Projet
                  </button>
                </Link>
                <Link to="/projets">
                  <button className="bg-black text-white px-8 py-4 text-lg rounded-lg hover:bg-gray-900 border-2 border-black shadow-lg flex items-center gap-2">
                    <Building2 className="w-5 h-5" />Voir les Projets
                  </button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Features */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div key={feature.title} initial={{ opacity:0,y:20 }} whileInView={{ opacity:1,y:0 }} transition={{ delay:index*0.1 }} viewport={{ once:true }}>
                <div className="border-2 border-gray-200 shadow-lg hover:shadow-xl hover:border-[#c41e3a] transition-all h-full p-6 bg-white rounded-xl">
                  <div className="w-14 h-14 bg-black rounded-lg flex items-center justify-center mb-4">
                    <feature.icon className="w-7 h-7 text-[#c41e3a]" />
                  </div>
                  <h3 className="text-xl font-bold mb-2 text-black">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Active Campaigns */}
      {activeCampaigns.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">Campagnes de Préfinancement en Cours</h2>
              <p className="text-xl text-gray-600">Investissez dès maintenant et recevez votre terrain proportionnel</p>
              <div className="w-24 h-1 bg-[#c41e3a] mx-auto mt-4"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {activeCampaigns.map((campaign) => (
                <motion.div key={campaign.id} initial={{ opacity:0,scale:0.95 }} whileInView={{ opacity:1,scale:1 }} viewport={{ once:true }}>
                  <div className="overflow-hidden border-2 border-gray-200 shadow-xl hover:shadow-2xl hover:border-[#c41e3a] transition-all rounded-xl">
                    {campaign.image && (
                      <div className="h-48 bg-gradient-to-br from-black to-gray-800 relative overflow-hidden">
                        <img src={campaign.image} alt={campaign.title} className="w-full h-full object-cover opacity-70" />
                        <div className="absolute top-4 right-4 bg-[#c41e3a] text-white px-3 py-1 rounded-full text-sm font-bold">En Cours</div>
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="text-2xl font-bold mb-3 text-black">{campaign.title}</h3>
                      <p className="text-gray-600 mb-4 line-clamp-2">{campaign.description}</p>
                      <div className="mb-4">
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-gray-600">Progression</span>
                          <span className="font-bold text-black">{((campaign.raised_amount / campaign.target_amount) * 100).toFixed(0)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div className="accent-gradient h-3 rounded-full" style={{ width: `${Math.min((campaign.raised_amount / campaign.target_amount) * 100, 100)}%` }}></div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                          <p className="text-gray-500">Objectif</p>
                          <p className="font-bold text-black">{campaign.target_amount?.toLocaleString()} FCFA</p>
                        </div>
                        <div className="bg-red-50 p-3 rounded-lg border border-[#c41e3a]">
                          <p className="text-gray-500">Collecté</p>
                          <p className="font-bold text-[#c41e3a]">{campaign.raised_amount?.toLocaleString()} FCFA</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                          <p className="text-gray-500">Minimum</p>
                          <p className="font-bold text-black">{campaign.min_contribution?.toLocaleString()} FCFA</p>
                        </div>
                        <div className="bg-red-50 p-3 rounded-lg border border-[#c41e3a]">
                          <p className="text-gray-500">Récompense</p>
                          <p className="font-bold text-[#c41e3a]">{campaign.reward_per_1000} m² / 1000 FCFA</p>
                        </div>
                      </div>
                      <Link to={`/campaigndetails?id=${campaign.id}`}>
                        <button className="w-full accent-gradient text-white py-3 rounded-lg hover:opacity-90 shadow-lg flex items-center justify-center gap-2">
                          Participer Maintenant<ArrowRight className="w-4 h-4" />
                        </button>
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/prefinancements">
                <button className="border-2 border-black text-black px-8 py-3 rounded-lg hover:bg-black hover:text-white shadow-lg flex items-center gap-2 mx-auto">
                  Voir Toutes les Campagnes<ArrowRight className="w-4 h-4" />
                </button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Featured Projects */}
      {featuredProjects.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">Projets en Vedette</h2>
              <p className="text-xl text-gray-600">Découvrez nos meilleurs terrains et maisons disponibles</p>
              <div className="w-24 h-1 bg-[#c41e3a] mx-auto mt-4"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuredProjects.map((project, index) => (
                <motion.div key={project.id} initial={{ opacity:0,y:30 }} whileInView={{ opacity:1,y:0 }} transition={{ delay:index*0.1 }} viewport={{ once:true }}>
                  <div className="overflow-hidden border-2 border-gray-200 shadow-lg hover:shadow-2xl hover:border-[#c41e3a] transition-all group rounded-xl bg-white flex flex-col">
                    <div className="relative h-56 overflow-hidden bg-gradient-to-br from-black to-gray-800">
                      {project.featured_image ? (
                        <img src={project.featured_image} alt={project.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><Building2 className="w-20 h-20 text-white/30" /></div>
                      )}
                      <div className="absolute top-4 left-4 bg-[#c41e3a] text-white px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                        <Star className="w-4 h-4" />Vedette
                      </div>
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-2 text-gray-600 mb-2"><MapPin className="w-4 h-4" /><span className="text-sm">{project.location}</span></div>
                      <h3 className="text-xl font-bold mb-2 text-black">{project.title}</h3>
                      <p className="text-gray-600 mb-4 line-clamp-2 flex-1">{project.description}</p>
                      <div className="flex justify-between items-center mb-4 pb-4 border-t border-gray-200 pt-4">
                        <div><p className="text-sm text-gray-500">Surface</p><p className="font-bold text-black">{project.area_m2} m²</p></div>
                        <div><p className="text-sm text-gray-500">Prix</p><p className="font-bold text-[#c41e3a]">{project.price?.toLocaleString()} FCFA</p></div>
                      </div>
                      <Link to={`/projectdetails?id=${project.id}`}>
                        <button className="w-full border-2 border-black text-black py-2 rounded-lg hover:bg-black hover:text-white flex items-center justify-center gap-2">
                          Voir les Détails<ArrowRight className="w-4 h-4" />
                        </button>
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/projets">
                <button className="accent-gradient text-white px-8 py-4 text-lg rounded-lg hover:opacity-90 shadow-lg flex items-center gap-2 mx-auto">
                  Voir Tous les Projets<ArrowRight className="w-5 h-5" />
                </button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Why Us */}
      <section className="py-16 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Pourquoi Choisir R2B Entreprises ?</h2>
              <div className="space-y-4">
                {["Terrains légalement titrés et sécurisés","Processus 100% transparent et digitalisé","Préfinancement participatif innovant","Documentation complète et traçable","Accompagnement personnalisé et professionnel","Équipe d'experts expérimentée à votre service"].map((item, index) => (
                  <motion.div key={index} initial={{ opacity:0,x:-20 }} whileInView={{ opacity:1,x:0 }} transition={{ delay:index*0.1 }} viewport={{ once:true }} className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-[#c41e3a] flex-shrink-0" />
                    <span className="text-lg">{item}</span>
                  </motion.div>
                ))}
              </div>
              <div className="mt-8">
                <Link to="/apropos">
                  <button className="accent-gradient text-white px-8 py-4 rounded-lg hover:opacity-90 shadow-lg flex items-center gap-2">
                    En Savoir Plus<ArrowRight className="w-4 h-4" />
                  </button>
                </Link>
              </div>
            </div>
            <div className="aspect-square bg-white/10 rounded-2xl p-8 border-2 border-white/20">
              <div className="grid grid-cols-2 gap-6 h-full">
                {[{number:"500+",label:"Clients Satisfaits"},{number:"50+",label:"Projets Réalisés"},{number:"1000+",label:"Lots Disponibles"},{number:"100%",label:"Sécurisés"}].map((stat, index) => (
                  <motion.div key={index} initial={{ opacity:0,scale:0.8 }} whileInView={{ opacity:1,scale:1 }} transition={{ delay:index*0.1 }} viewport={{ once:true }}
                    className="bg-white/10 rounded-xl p-6 flex flex-col items-center justify-center text-center border border-white/20">
                    <p className="text-4xl font-bold text-[#c41e3a] mb-2">{stat.number}</p>
                    <p className="text-sm">{stat.label}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-[#c41e3a] to-[#a61c3c] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Prêt à Investir dans Votre Avenir ?</h2>
          <p className="text-xl mb-8">Rejoignez des centaines d'investisseurs qui ont fait confiance à R2B Entreprises</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/prefinancements">
              <button className="bg-black text-white px-8 py-4 text-lg rounded-lg hover:bg-gray-900 shadow-lg flex items-center gap-2">
                Commencer Maintenant<ArrowRight className="w-5 h-5" />
              </button>
            </Link>
            <Link to="/contact">
              <button className="bg-white text-[#c41e3a] px-8 py-4 text-lg rounded-lg hover:bg-gray-100 border-2 border-white shadow-lg flex items-center gap-2">
                <Phone className="w-5 h-5" />Nous Contacter
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
